import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@services/firebase';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Grid,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { orgAdminRegistrationSchema } from '@utils/validators';
import PasswordStrengthIndicator from '@components/auth/PasswordStrengthIndicator';
import { useAuth } from '@hooks/useAuth';
import { createOrganization } from '@services/organizationService';
import { updateUserDocument } from '@services/userService';

const OrgAdminRegistrationForm = ({ googleUser = null }) => {
  const navigate = useNavigate();
  const { signup, completeGoogleSignup } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Create conditional validation schema based on auth method
  const validationSchema = googleUser
    ? orgAdminRegistrationSchema.omit(['password', 'confirmPassword'])
    : orgAdminRegistrationSchema;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      displayName: googleUser?.displayName || '',
      email: googleUser?.email || '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      organizationName: '',
      organizationDescription: '',
      website: '',
      contactEmail: googleUser?.email || '',
      contactPhone: '',
      addressStreet: '',
      addressCity: '',
      addressState: '',
      addressZipCode: '',
    },
  });

  // Update form values when googleUser changes
  useEffect(() => {
    if (googleUser) {
      setValue('displayName', googleUser.displayName || '');
      setValue('email', googleUser.email || '');
      setValue('contactEmail', googleUser.email || '');
    }
  }, [googleUser, setValue]);

  const onSubmit = async (data) => {
    try {
      setError('');
      setLoading(true);

      // reCAPTCHA v3 verification
      if (executeRecaptcha) {
        const token = await executeRecaptcha('org_admin_registration');
        const verifyRecaptcha = httpsCallable(functions, 'verifyRecaptcha');
        const result = await verifyRecaptcha({ token });
        if (!result.data.success) {
          setError('Bot check failed. Please try again.');
          setLoading(false);
          return;
        }
      }

      console.log('🔵 Step 0: Starting registration...');

      let userId;
      let userEmail;

      // Step 1: Create user account
      if (googleUser) {
        console.log('🔵 Step 1: Using Google user');
        // For Google users, we'll create the org first, then complete signup
        userId = googleUser.uid;
        userEmail = googleUser.email;
      } else {
        console.log('🔵 Step 1: Creating email/password user...');
        // Create email/password account first
        const result = await signup(data.email, data.password, {
          displayName: data.displayName,
          userType: 'org-admin',
          phoneNumber: data.phoneNumber || '',
          status: 'active', // Active immediately (can be changed to 'trial', 'expired' for future subscription features)
          organizationId: null, // Will be set after org creation
          organizationName: null,
        });
        console.log('✅ Step 1 complete: User created', result.user.uid);
        userId = result.user.uid;
        userEmail = result.user.email;
      }

      // Step 2: Create organization
      console.log('🔵 Step 2: Creating organization...');
      const organizationData = {
        organizationName: data.organizationName,
        organizationDescription: data.organizationDescription || '',
        website: data.website || '',
        contactEmail: data.contactEmail || userEmail,
        contactPhone: data.contactPhone || data.phoneNumber || '',
        address: {
          street: data.addressStreet || '',
          city: data.addressCity || '',
          state: data.addressState || '',
          zipCode: data.addressZipCode || '',
        },
      };

      const organizationId = await createOrganization(organizationData, userId);
      console.log('✅ Step 2 complete: Organization created', organizationId);

      // Step 3: Update user document or complete Google signup
      if (googleUser) {
        console.log('🔵 Step 3: Completing Google signup...');
        await completeGoogleSignup(googleUser, {
          displayName: data.displayName,
          userType: 'org-admin',
          role: 'owner',
          phoneNumber: data.phoneNumber || '',
          status: 'active', // Active immediately (can be changed to 'trial', 'expired' for future subscription features)
          organizationId,
          organizationName: data.organizationName,
        });
        console.log('✅ Step 3 complete');
      } else {
        // For email/password users, update the user document with org info
        console.log('🔵 Step 3: Updating user with organization info...');
        await updateUserDocument(userId, {
          organizationId,
          organizationName: data.organizationName,
          role: 'owner',
        });
        console.log('✅ Step 3 complete: User updated with organization info');
      }

      console.log('✅ Registration complete! Navigating to dashboard...');
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('❌ Registration error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ width: '100%' }}
    >
      <Typography variant="h5" component="h2" gutterBottom>
        Organization Admin Registration
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create a new organization and become an admin. Your organization will be reviewed before activation.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Personal Information Section */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Personal Information
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Full Name"
            {...register('displayName')}
            error={!!errors.displayName}
            helperText={errors.displayName?.message}
            disabled={loading || !!googleUser}
            InputLabelProps={{ shrink: !!googleUser || undefined }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            {...register('email')}
            error={!!errors.email}
            helperText={errors.email?.message}
            disabled={loading || !!googleUser}
            InputLabelProps={{ shrink: !!googleUser || undefined }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone Number (Optional)"
            {...register('phoneNumber')}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber?.message}
            disabled={loading}
          />
        </Grid>

        {/* Password fields (only for email/password registration) */}
        {!googleUser && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                {...register('password')}
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <PasswordStrengthIndicator password={watch('password')} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                {...register('confirmPassword')}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Organization Information Section */}
      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Organization Information
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Organization Name"
            {...register('organizationName')}
            error={!!errors.organizationName}
            helperText={errors.organizationName?.message}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Organization Description (Optional)"
            multiline
            rows={3}
            {...register('organizationDescription')}
            error={!!errors.organizationDescription}
            helperText={errors.organizationDescription?.message || 'Tell us about your organization'}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Website (Optional)"
            {...register('website')}
            error={!!errors.website}
            helperText={errors.website?.message}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Contact Email (Optional)"
            type="email"
            {...register('contactEmail')}
            error={!!errors.contactEmail}
            helperText={errors.contactEmail?.message}
            disabled={loading}
            InputLabelProps={{ shrink: !!googleUser || undefined }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Contact Phone (Optional)"
            {...register('contactPhone')}
            error={!!errors.contactPhone}
            helperText={errors.contactPhone?.message}
            disabled={loading}
          />
        </Grid>

        {/* Address */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Street Address (Optional)"
            {...register('addressStreet')}
            error={!!errors.addressStreet}
            helperText={errors.addressStreet?.message}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="City (Optional)"
            {...register('addressCity')}
            error={!!errors.addressCity}
            helperText={errors.addressCity?.message}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="State (Optional)"
            {...register('addressState')}
            error={!!errors.addressState}
            helperText={errors.addressState?.message}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="ZIP Code (Optional)"
            {...register('addressZipCode')}
            error={!!errors.addressZipCode}
            helperText={errors.addressZipCode?.message}
            disabled={loading}
          />
        </Grid>
      </Grid>

      {/* Submit Button */}
      <Button
        fullWidth
        type="submit"
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ mt: 4 }}
      >
        {loading ? 'Creating Organization...' : 'Create Organization'}
      </Button>

      <Alert severity="info" sx={{ mt: 2 }}>
        Your organization will be reviewed by our team before activation. You'll be notified once approved.
      </Alert>
    </Box>
  );
};

export default OrgAdminRegistrationForm;
